import React, { useEffect, useRef } from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import Image from 'react-bootstrap/lib/Image';
import classnames from 'classnames';

import { DataDtmActions, Devices, ImageTypes } from '../Constants';
import Utils from '../Utils';
import Classes from './../Classes';
import { IconButton } from '../form';
import ProductImageClip from './ProductImageClip';
import mixpanelConstants from '../mixpanelConstants';
import classNames from 'classnames';
import { trackImpressions, trackMixpanelEvent } from '../MixpanelHelper';
import VideoPlayIcon from '../components/VideoPlayIcon';
import ProcessingVideoIcon from '../components/ProcessingVideoIcon';

const ProductImage = ({
  product,
  src,
  alt,
  onClick,
  imageSize,
  deviceType,
  isEditable,
  translations,
  tags,
  isInnovative,
  isRounded,
  imageDataDtm,
  imageIndex,
  fallbackImage,
  trackProductInteractedEvent,
  isNew,
  isOverlay,
  mixpanelProps,
  isVideoAvailable,
}) => {
  const imageUrl = Utils.getResizedImage(
    src,
    deviceType || Devices.desktop,
    imageSize
  );
  const fbImage =
    fallbackImage ||
    Utils.getResizedImage(src, Devices.desktop, ImageTypes.product);
  const clickable = onClick ? Classes.clickable : null;
  const [isErrorImage, setIsErrorImage] = useState(false);
  const productRef = useRef();
  const VideoStatus = Object.freeze({
    NOT_STARTED: 'NotStarted',
    STARTED: 'Started',
    IN_PROGRESS: 'Inprogress',
  });

  let retry = 1;
  const onImageError = (e) => {
    if (e.target.src === fbImage) {
      return;
    }
    if (retry >= 3) {
      e.target.src = fbImage;
      setIsErrorImage(true);
      return;
    }
    e.target.src = imageUrl;
    retry += 1;
  };

  useEffect(() => {
    let observer;

    if (mixpanelProps) {
      observer = trackImpressions(
        mixpanelConstants.eventName.productImpression,
        {
          ...mixpanelProps,
          viewedElement: product.video?.id ? mixpanelConstants.element.productVideo : mixpanelConstants.element.productImage
        },
        0
      );

      if (productRef.current) {
        observer.observe(productRef.current);
      }
    }

    return () => {
      observer?.disconnect();
    };
  }, []);

  return (
    <div
      className={classNames(
        Classes.relative,
        isErrorImage ? Classes.overlayProductErrorImage : null
      )}
      onClick={() =>
        trackProductInteractedEvent
          ? trackProductInteractedEvent(product?.video?.id ? mixpanelConstants.element.productVideo : mixpanelConstants.element.productImage)
          : undefined
      }
      ref={productRef}
    >
      {[
        VideoStatus.NOT_STARTED,
        VideoStatus.STARTED,
        VideoStatus.IN_PROGRESS,
      ].includes(product?.video?.status) && (
        <ProcessingVideoIcon
          text={
            translations?.processingVideoText ||
            translations?.products?.processingVideoText
          }
        />
      )}
      <Image
        responsive
        className={classnames(Classes.centerBlock, clickable, {
          [Classes.roundCorners]: isRounded,
        })}
        src={`${imageUrl}?${new Date().getTime()}`}
        alt={alt}
        onClick={() => {
          if (imageIndex || imageIndex === 0) {
            onClick(imageIndex);
          } else {
            onClick();
          }
        }}
        onError={onImageError}
        data-dtm={imageDataDtm?.tag}
        data-dtm-actions={imageDataDtm ? DataDtmActions.click : null}
        data-dtm-attributes={imageDataDtm?.attributes}
      />
      {product?.video?.customThumbnailUrl ||
      product?.video?.defaultThumbnailUrl ? (
        <VideoPlayIcon onClick={onClick}></VideoPlayIcon>
      ) : null}
      {isInnovative ? (
        <div>
          <ProductImageClip text={translations.innovativeClipText} />
        </div>
      ) : null}
      {isNew ? (
        <div>
          <ProductImageClip text={translations.newProductClipText} />
        </div>
      ) : null}
      {isEditable ? (
        <IconButton
          tag={tags.edit}
          ariaLabel={translations.buttons.edit}
          className={classnames(Classes.overlayIcon, Classes.secondary01Button)}
          onClick={() => {
            if (imageIndex || imageIndex === 0) {
              onClick(imageIndex);
            } else {
              onClick();
            }
            trackMixpanelEvent(mixpanelConstants.eventName.productEdited, {
              element: mixpanelConstants.element.productEdited,
              packageName: sessionStorage.getItem('packageType'),
              component: sessionStorage.getItem('component'),
              exhibitorId: sessionStorage.getItem('exhibitorId'), 
              exhibitorName: sessionStorage.getItem('exhibitorName'),
              productId: product?.id, 
              productName: product?.name,
            });
          }
          }
          iconClass={classnames(Classes.icons.edit, Classes.icons.large)}
        />
      ) : (
        ''
      )}
      {isErrorImage && isOverlay ? (
        <div className={Classes.overlayErrorImageHelpText}>
          <div>
            <span>{translations.products.errorImageHelpTextMessage}</span>
          </div>
          <div>
            <span>{translations.products.errorImageHelpTextSecondMessage}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

ProductImage.propTypes = {
  src: PropTypes.string.isRequired,
  imageSize: PropTypes.string.isRequired,
  deviceType: PropTypes.string,
  alt: PropTypes.string,
  onClick: PropTypes.func,
  tags: PropTypes.shape({
    edit: PropTypes.string,
  }),
  isEditable: PropTypes.bool,
  isRounded: PropTypes.bool,
  translations: PropTypes.shape({
    buttons: PropTypes.shape({
      edit: PropTypes.string,
    }),
    innovativeClipText: PropTypes.string,
    newProductClipText: PropTypes.string,
  }).isRequired,
  isInnovative: PropTypes.bool,
  imageIndex: PropTypes.number,
  trackProductInteractedEvent: PropTypes.func,
  isNew: PropTypes.bool,
  isOverlay: PropTypes.bool,
  mixpanelProps: PropTypes.object,
  isVideoAvailable: PropTypes.bool,
};

export default ProductImage;
